import type { MergeabilityStatus } from './get-mergeability-status.server';

import { isBlockedByBranchRules } from './get-mergeability-status.server';

export type MergeabilityStatusTone = 'merged' | 'neutral' | 'success' | 'warning' | 'danger';

const BADGE_CLASSES_BY_TONE: Record<MergeabilityStatusTone, string> = {
  merged: 'bg-purple-900/50 text-purple-300',
  neutral: 'bg-zinc-800 text-zinc-300',
  success: 'bg-green-900/50 text-green-300',
  warning: 'bg-yellow-900/50 text-yellow-300',
  danger: 'bg-red-900/50 text-red-300',
};

const DOT_CLASSES_BY_TONE: Record<MergeabilityStatusTone, string> = {
  merged: 'bg-purple-400',
  neutral: 'bg-zinc-500',
  success: 'bg-green-700',
  warning: 'bg-yellow-700',
  danger: 'bg-red-500/90',
};

export function getMergeabilityStatusTone(
  mergeabilityStatus: MergeabilityStatus,
): MergeabilityStatusTone {
  if (mergeabilityStatus.state.merged) {
    return 'merged';
  }
  if (mergeabilityStatus.draft) {
    return 'neutral';
  }
  if (mergeabilityStatus.state.prState === 'closed') {
    return 'danger';
  }
  if (mergeabilityStatus.canMerge) {
    return 'success';
  }
  const statusText = mergeabilityStatus.humanReadableMergeStatus;
  const approvalStatus = mergeabilityStatus.approvalStatus;
  const hasMissingApprovalRequirement = approvalStatus
    ? !approvalStatus.passed && !approvalStatus.hasBlockingChangeRequests
    : statusText === 'Approval required' || statusText === 'Approvals required';
  // Branch-rule blocks are usually a pending code owner review, which waits like a missing
  // approval rather than fails like a conflict. So does a pull request in the merge queue.
  if (
    hasMissingApprovalRequirement ||
    statusText.startsWith('Required CI in progress') ||
    isBlockedByBranchRules(mergeabilityStatus) ||
    mergeabilityStatus.mergeQueue === 'queued'
  ) {
    return 'warning';
  }
  return 'danger';
}

export function getMergeabilityStatusBadgeClasses(mergeabilityStatus: MergeabilityStatus) {
  return BADGE_CLASSES_BY_TONE[getMergeabilityStatusTone(mergeabilityStatus)];
}

export function getMergeabilityStatusDotClasses(mergeabilityStatus: MergeabilityStatus) {
  return DOT_CLASSES_BY_TONE[getMergeabilityStatusTone(mergeabilityStatus)];
}

export function getMergeabilityStatusBadgeText(mergeabilityStatus: MergeabilityStatus) {
  return mergeabilityStatus.humanReadableMergeStatus;
}
